import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Building, Tag, FileText, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';

const TransactionDetails = ({
  extractedData,
  prefillData,
  onSubmit,
  onBack,
  isSubmitting
}) => {
  const [formData, setFormData] = useState({
    transactionDate: new Date().toISOString().split('T')[0],
    amount: '',
    vendor: '',
    category: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const categories = [
    'Food & Dining',
    'Gas & Fuel',
    'Groceries',
    'Shopping',
    'Entertainment',
    'Travel',
    'Medical',
    'Office Supplies',
    'Restaurant Supplies',
    'Utilities',
    'Other'
  ];

  // Update form data when extracted data or prefill data changes
  useEffect(() => {
    const newFormData = { ...formData };

    // First, apply prefill data if available
    if (prefillData) {
      if (prefillData.transactionDate) newFormData.transactionDate = prefillData.transactionDate;
      if (prefillData.amount) newFormData.amount = prefillData.amount;
      if (prefillData.vendor) newFormData.vendor = prefillData.vendor;
    }

    // Then, apply extracted data (overrides prefill data)
    if (extractedData) {
      if (extractedData.date) newFormData.transactionDate = extractedData.date;
      if (extractedData.amount) newFormData.amount = extractedData.amount.toString();
      if (extractedData.vendor) newFormData.vendor = extractedData.vendor;
    }

    setFormData(newFormData);
  }, [extractedData, prefillData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.transactionDate) {
      newErrors.transactionDate = 'Transaction date is required';
    }

    if (formData.amount === '' || isNaN(formData.amount) || parseFloat(formData.amount) === 0) {
      newErrors.amount = 'Please enter a valid amount (use a negative amount for returns)';
    }

    if (!formData.vendor.trim()) {
      newErrors.vendor = 'Vendor name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.notes.trim()) {
      newErrors.notes = 'Notes are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const transactionData = {
      ...formData,
      amount: parseFloat(formData.amount),
      receiptImages: extractedData?.receiptImages || (extractedData?.receiptImage ? [extractedData.receiptImage] : [])
    };

    try {
      await onSubmit(transactionData);
    } catch (error) {
      // Error handled by parent component
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 80) return CheckCircle;
    return AlertTriangle;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Upload
        </button>
      </div>

      {/* Extraction Results Banner */}
      {extractedData && (() => {
        const wasScanned = !!(extractedData.vendor || extractedData.amount || extractedData.date);
        return wasScanned ? (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Receipt Scanned Successfully
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  We automatically extracted the following information. Please verify it's correct.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Auto-fill unavailable
                </h3>
                <div className="mt-1 text-sm text-yellow-700">
                  Please enter the date, amount, and vendor manually.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Transaction Date */}
          <div>
            <label htmlFor="transactionDate" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Transaction Date
              {extractedData?.date && (
                <span className="ml-2 text-xs text-green-600">(Auto-detected)</span>
              )}
            </label>
            <input
              type="date"
              id="transactionDate"
              name="transactionDate"
              value={formData.transactionDate}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border ${errors.transactionDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
              required
            />
            {errors.transactionDate && (
              <p className="mt-1 text-sm text-red-600">{errors.transactionDate}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="h-4 w-4 inline mr-1" />
              Amount
              {extractedData?.amount && (
                <span className="ml-2 text-xs text-green-600">(Auto-detected)</span>
              )}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="amount"
                name="amount"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                className={`block w-full pl-7 pr-3 py-2 border ${errors.amount ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                required
              />
            </div>
            {errors.amount ? (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">For a return or refund, enter a negative amount (e.g. -6.47).</p>
            )}
          </div>
        </div>

        {/* Vendor */}
        <div>
          <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="h-4 w-4 inline mr-1" />
            Vendor/Merchant
            {extractedData?.vendor && (
              <span className="ml-2 text-xs text-green-600">(Auto-detected)</span>
            )}
          </label>
          <input
            type="text"
            id="vendor"
            name="vendor"
            value={formData.vendor}
            onChange={handleChange}
            placeholder="e.g., Starbucks, Shell Gas Station, Amazon"
            className={`block w-full px-3 py-2 border ${errors.vendor ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
            required
          />
          {errors.vendor && (
            <p className="mt-1 text-sm text-red-600">{errors.vendor}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
            <Tag className="h-4 w-4 inline mr-1" />
            Category
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`block w-full px-3 py-2 border ${errors.category ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
            required
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-600">{errors.category}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            <FileText className="h-4 w-4 inline mr-1" />
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={formData.notes}
            onChange={handleChange}
            placeholder="Additional details about this transaction..."
            className={`block w-full px-3 py-2 border ${errors.notes ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
            required
          />
          {errors.notes && (
            <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Transaction'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionDetails;